import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";
/* primary alias para substituições globais */

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 0 2em 2em 2em;
  box-sizing: border-box;
  color: ${({ theme }) => theme.text};
  gap: 0;
  position: relative;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * {
    margin: 0;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 1em;
  }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "MOVIMENTAÇÕES · NF-e / CT-e";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          ${mono};
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-size: 0.9em;
          background: transparent !important;
          padding: 0 !important;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 0;
    margin-top: 0;
    margin-bottom: 14px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 11px 16px;
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      border-radius: 0;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      ${mono};
      font-size: 0.66em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover { opacity: 0.8; }

      &.active {
        opacity: 1;
        color: ${tokens.brand};
        border-bottom-color: ${tokens.brand};
        background: transparent;
        box-shadow: none;
      }

      svg { font-size: 1.2em; }
    }
  }

  /* ========== TOOLBAR ========== */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin: 0.8em 0;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      flex-wrap: wrap;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .toolbar-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover:not(:disabled) {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.active {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
        opacity: 1;
      }

      &.icon-only { padding: 8px 10px; }

      &:disabled { opacity: 0.4; cursor: not-allowed; }

      svg { font-size: 0.95em; }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      min-width: 150px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
      }

      input {
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        width: 100%;

        &::placeholder { opacity: 0.4; }
        &:focus { outline: none; }
      }
    }

    .docs-count {
      ${mono};
      font-size: 0.7em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      padding: 0 8px;
    }
  }

  .active-filters {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .active-filters-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.9em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      font-size: 0.75em;

      svg {
        font-size: 1.1em;
        color: ${tokens.brand};
      }
    }

    .active-filters-badges {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      flex: 1;

      .filter-badge-active {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 14px;
        background: linear-gradient(135deg, ${tokens.brand}12 0%, ${tokens.brand}08 100%);
        border: 1.5px solid ${tokens.brand}25;
        border-radius: 12px;
        font-size: 0.875em;
        color: ${({ theme }) => theme.text};
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 3px;
          height: 100%;
          background: ${tokens.brand};
          opacity: 0.6;
        }

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-color: ${tokens.brand}40;
          background: linear-gradient(135deg, ${tokens.brand}18 0%, ${tokens.brand}12 100%);
        }

        span {
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.9;
        }

        &.empresas-filter {
          .empresas-thumbs {
            display: flex;
            gap: 4px;
            align-items: center;

            .empresa-thumb {
              width: 24px;
              height: 24px;
              border-radius: 6px;
              border: 1.5px solid;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
              transition: all 0.2s ease;

              svg {
                font-size: 12px;
              }

              &:hover {
                transform: scale(1.1);
              }
            }
          }
        }

        button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${({ theme }) => theme.text}08;
          border: none;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.2s ease;
          margin-left: 4px;

          &:hover {
            opacity: 1;
            background: ${({ theme }) => theme.danger}15;
            color: ${({ theme }) => theme.danger};
            transform: rotate(90deg);
          }

          svg {
            font-size: 0.85em;
          }
        }
      }
    }
  }

  /* ========== TABLE CONTAINER ========== */
  .table-container {
    ${({ theme }) => cardBase(theme)};
    border-radius: ${tokens.radiusLg};
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 500px;
    margin: 0;
    padding: 0;

    .table-header {
      display: flex;
      align-items: center;
      background: ${({ theme }) => surface(theme, 1)};
      padding: 12px 20px;
      gap: 16px;

      .header-cell {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.75em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
        user-select: none;

        svg {
          font-size: 1em;
          opacity: 0.5;

          &.sort-icon {
            opacity: 0.3;
          }
        }

        &.sortable {
          cursor: pointer;
          transition: opacity 0.2s ease;

          &:hover {
            opacity: 1;
          }

          .sort-icon {
            font-size: 1.1em;
            opacity: 0.3;
            color: ${({ theme }) => theme.text};
            transition: all 0.2s ease;
            flex-shrink: 0;
            margin-left: 2px;

            &.active {
              opacity: 1;
              color: ${tokens.brand};
            }
          }

          &:hover .sort-icon:not(.active) {
            opacity: 0.5;
          }
        }

        &.col-dot {
          width: auto;
          min-width: 40px;
        }
        &.col-data {
          min-width: 100px;
          width: 100px;
        }
        &.col-numero {
          min-width: 100px;
          width: 100px;
        }
        &.col-chave {
          min-width: 220px;
          width: 220px;
        }
        &.col-status {
          min-width: 100px;
          width: 100px;
        }
        &.col-emitente-info {
          min-width: 250px;
          width: 250px;
        }
        &.col-destinatario-info {
          min-width: 250px;
          width: 250px;
        }
        &.col-cfop {
          min-width: 80px;
          width: 80px;
        }
        &.col-actions {
          width: 50px;
        }
      }
    }

    .table-body {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      flex: 1;
      min-height: 350px;
      max-height: 550px;
      position: relative;
      transition: opacity 0.2s ease;

      /* Scrollbar customizada */
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: ${({ theme }) => surface(theme, 1)};
        border-radius: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme)};
        border-radius: 6px;
        transition: background 0.2s ease;

        &:hover {
          background: ${({ theme }) => theme.text}33;
        }
      }

      /* Firefox */
      scrollbar-width: thin;
      scrollbar-color: ${({ theme }) => hairline(theme)}
        ${({ theme }) => surface(theme, 1)};

      .loading-state,
      .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .refreshing-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 80px;
        background: rgba(0, 0, 0, 0.03);
        pointer-events: none;
        z-index: 10;

        svg {
          font-size: 28px;
          color: ${tokens.brand};
          animation: ${spin} 1s linear infinite;
        }
      }

      .table-row {
        display: flex;
        align-items: center;
        background: ${({ theme }) => theme.tableRow};
        padding: 18px 20px;
        margin: 1px 0;
        gap: 16px;
        transition: background 0.2s ease;
        min-height: 70px;

        &:hover {
          background: ${({ theme }) => theme.tableRowHover};
        }

        .cell {
          display: flex;
          align-items: center;
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};

          &.col-dot {
            width: auto;
            min-width: 40px;
            display: flex;
            align-items: center;
            gap: 6px;

            .dot {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              flex-shrink: 0;

              &.green {
                background: #10b981;
                box-shadow: 0 0 6px rgba(16, 185, 129, 0.5);
              }

              &.blue {
                background: #3b82f6;
                box-shadow: 0 0 6px rgba(59, 130, 246, 0.5);
              }

              &.red {
                background: #ef4444;
                box-shadow: 0 0 6px rgba(239, 68, 68, 0.5);
              }
            }
          }

          &.col-data {
            width: 100px;
            opacity: 0.85;
          }

          &.col-numero {
            width: 100px;
            font-weight: 600;
          }

          &.col-chave {
            width: 220px;
            font-family: "SF Mono", "Consolas", "Monaco", monospace;
            font-size: 0.9em;
            opacity: 0.75;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          &.col-status {
            width: 100px;

            .status-badge {
              display: inline-block;
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 0.75em;
              font-weight: 500;

              &.authorized {
                background: rgba(16, 185, 129, 0.15);
                color: #10b981;
              }

              &.cancelled {
                background: rgba(239, 68, 68, 0.15);
                color: #ef4444;
              }
            }
          }

          &.col-emitente-info,
          &.col-destinatario-info {
            width: 250px;

            .stacked-info {
              display: flex;
              flex-direction: column;
              gap: 4px;
              width: 100%;
              overflow: hidden;

              .info-name {
                font-weight: 500;
                font-size: 0.95em;
                color: ${({ theme }) => theme.text};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
              }

              .info-cnpj {
                font-family: "SF Mono", "Consolas", "Monaco", monospace;
                font-size: 0.8em;
                opacity: 0.6;
              }
            }
          }

          &.col-cfop {
            width: 80px;
            font-family: "SF Mono", "Consolas", "Monaco", monospace;
            font-weight: 600;
            opacity: 0.85;
          }

          &.col-cnpj {
            width: 200px;
            font-family: "SF Mono", "Consolas", "Monaco", monospace;
            font-size: 0.9em;
            opacity: 0.85;
          }

          &.col-emitente {
            width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            opacity: 0.8;
          }

          &.col-actions {
            width: 50px;
            justify-content: center;

            .action-btn {
              background: transparent;
              border: none;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
              cursor: pointer;
              padding: 6px;
              border-radius: 6px;
              transition: all 0.2s ease;

              &:hover {
                opacity: 1;
                color: ${tokens.brand};
              }
            }
          }
        }
      }
    }
  }

  /* ========== MENSAL LAYOUT - CLEAN DESIGN ========== */
  .mensal-layout {
    display: flex;
    flex-direction: column;
    gap: 1.2em;
    padding-bottom: 2em;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme)};
      border-radius: 6px;
    }
  }

  /* Barra Superior com Seletor de Mês e Cards de Métricas */
  .mensal-top-bar {
    display: flex;
    align-items: center;
    gap: 1.5em;

    @media (max-width: 1000px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .month-selector {
    display: flex;
    align-items: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 12px;
    padding: 8px;
    gap: 4px;

    .month-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: transparent;
      border: none;
      border-radius: 12px;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: ${tokens.brand};
        color: #ffffff;
        opacity: 1;
      }

      svg {
        font-size: 1.4em;
      }
    }

    .month-display {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 16px;

      .month-icon {
        font-size: 1em;
        color: ${tokens.brand};
      }

      .month-text {
        font-size: 0.95em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
      }
    }
  }

  /* Cards de Métricas */
  .metric-cards {
    display: flex;
    gap: 0.8em;
    flex: 1;
    margin: 0;
    padding: 0;
    height: 70px;
    max-height: 70px;
    min-height: 70px;

    @media (max-width: 900px) {
      flex-wrap: wrap;
      height: auto;
      max-height: none;
      min-height: auto;
    }
  }

  .mensal-top-bar .metric-cards {
    margin-bottom: 0;
  }

  .metric-card {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 16px 18px;
    height: 70px;
    ${({ theme }) => cardBase(theme)};
    border-radius: ${tokens.radiusLg};
    min-width: 100px;
    position: relative;
    overflow: hidden;
    margin: 0;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .metric-icon {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1em;
      flex-shrink: 0;
      background: transparent;
      opacity: 0.85;

      /* Cor APENAS no ícone (thumb) */
      &.blue   { color: #3b82f6; }
      &.green  { color: ${tokens.brand}; }
      &.purple { color: #8b5cf6; }
      &.orange { color: ${tokens.warn}; }
      &.red    { color: ${tokens.danger}; }
    }

    .metric-content {
      display: flex;
      flex-direction: column;
      min-width: 0;
      gap: 2px;
    }

    .metric-value {
      ${mono};
      font-size: 1.45em;
      font-weight: 500;
      line-height: 1.05;
      letter-spacing: -0.02em;
      color: ${({ theme }) => theme.text};
    }

    .metric-label {
      ${eyebrow};
      font-size: 0.55em;
      opacity: 0.5;
    }
  }

  /* Seção do Gráfico de Barras */
  .chart-section {
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 12px;
    padding: 14px 20px;

    .recharts-bar-rectangle {
      opacity: 0.7;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }

    .chart-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;

      .chart-title {
        font-size: 0.85em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      .chart-legend {
        display: flex;
        gap: 16px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75em;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;

          .dot {
            width: 8px;
            height: 8px;
            border-radius: 2px;
          }
        }
      }
    }
  }

  /* Layout Principal: Tabelas + Gráficos */
  .mensal-main {
    display: flex;
    gap: 1em;
    height: 45em;

    @media (max-width: 900px) {
      flex-direction: column;
      height: auto;
    }
  }

  .tables-row {
    display: flex;
    flex: 1;
    gap: 1em;
    height: 100%;
    min-height: 0;

    @media (max-width: 1100px) {
      flex-direction: column;
    }

    .table-container.compact {
      display: flex;
      flex-direction: column;
      flex: 1;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: 16px;
      overflow: hidden;
      height: 100%;
      min-height: 0;

      .table-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 16px 24px;
        background: transparent;
        font-size: 0.9em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};

        .header-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${tokens.brand}15;
          border-radius: 12px;
          font-size: 1em;
          color: ${tokens.brand};
        }
      }

      .table-inner {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        padding: 0 8px 8px;
      }

      .table-row {
        display: grid;
        grid-template-columns: 1.2fr repeat(6, 1fr);
        align-items: center;
        padding: 0 16px;
        min-height: 44px;
        transition: background 0.15s ease;
        flex-shrink: 0;

        &:not(:last-child):not(.header) {
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
        }

        &:hover:not(.header) {
          background: ${({ theme }) => surface(theme, 1)};
        }

        &.header {
          background: ${({ theme }) => surface(theme, 0)};
          min-height: 40px;
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
          position: sticky;
          top: 0;
          z-index: 1;
        }
      }

      .table-body {
        flex: 1;
        overflow-y: auto;
        background: ${({ theme }) => surface(theme, 1)};
        border-radius: 0 0 12px 12px;

        &::-webkit-scrollbar {
          width: 4px;
        }
        &::-webkit-scrollbar-track {
          background: transparent;
        }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme)};
          border-radius: 6px;
        }
      }

      .header-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        font-size: 0.68em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        text-transform: uppercase;
        letter-spacing: 0.8px;

        &.col-data {
          justify-content: flex-start;
        }

        svg {
          font-size: 1em;
          opacity: 0.6;
        }
      }

      .cell {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.82em;
        color: ${({ theme }) => theme.text};
        font-weight: 500;
        opacity: 0.7;

        &.col-data {
          justify-content: flex-start;
          font-weight: 600;
          opacity: 0.85;
          font-size: 0.85em;
        }
      }
    }
  }

  /* Coluna de Gráficos Donut */
  .charts-column {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 220px;
    flex-shrink: 0;
    height: 100%;

    @media (max-width: 900px) {
      width: 100%;
      flex-direction: row;
    }

    @media (max-width: 500px) {
      flex-direction: column;
    }

    .chart-card {
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: 14px;
      padding: 16px;

      .chart-header {
        margin-bottom: 6px;

        .chart-title {
          font-size: 0.8em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .chart-content {
        display: flex;
        justify-content: center;
      }

      .chart-legend {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 8px;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;

          .legend-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
          }

          .legend-label {
            font-size: 0.7em;
            color: ${({ theme }) => theme.text};
            opacity: 0.7;
            flex: 1;
          }

          .legend-value {
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
          }
        }
      }
    }
  }

  /* ========== FILTER SIDEBAR ========== */
  .filter-sidebar {
    position: fixed;
    top: 0;
    left: -380px;
    width: 380px;
    height: 100vh;
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "#ffffff"
        : "#0d0f14"};
    border-right: 1px solid ${({ theme }) => hairline(theme)};
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.open {
      left: 0;
    }

    .filter-sidebar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      h4 {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        font-size: 1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};

        svg {
          font-size: 1.1em;
          opacity: 0.7;
        }
      }

      .close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: ${({ theme }) => surface(theme, 1)};
        border-radius: 6px;
        color: ${({ theme }) => theme.text};
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: ${({ theme }) => hairline(theme)};
        }

        svg {
          font-size: 1.2em;
        }
      }
    }

    .filter-sidebar-content {
      flex: 1;
      overflow-y: auto;
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 20px;

      .filter-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
        position: relative;

        .emitente-suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: ${({ theme }) => surface(theme, 0)};
          border: 1.5px solid ${tokens.brand}40;
          border-top: none;
          border-radius: 0 0 12px 12px;
          box-shadow: 
            0 12px 40px rgba(0, 0, 0, 0.25),
            0 4px 12px rgba(0, 0, 0, 0.15),
            0 0 0 1px ${tokens.brand}10;
          margin-top: 0;
          z-index: 99999;
          max-height: 320px;
          overflow-x: hidden;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          backdrop-filter: blur(10px);
          animation: slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Scrollbar customizada */
          &::-webkit-scrollbar {
            width: 6px;
          }

          &::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 12px;
          }

          &::-webkit-scrollbar-thumb {
            background: ${tokens.brand}40;
            border-radius: 12px;
            transition: background 0.2s ease;

            &:hover {
              background: ${tokens.brand}60;
            }
          }

          /* Firefox */
          scrollbar-width: thin;
          scrollbar-color: ${tokens.brand}40 transparent;

          .suggestion-item {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 14px 16px;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            border-bottom: 1px solid ${({ theme }) => hairline(theme)}30;
            position: relative;

            &:last-child {
              border-bottom: none;
              border-bottom-left-radius: 12px;
              border-bottom-right-radius: 12px;
            }

            &::before {
              content: '';
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              width: 3px;
              background: ${tokens.brand};
              opacity: 0;
              transition: opacity 0.2s ease;
            }

            &:hover {
              background: ${tokens.brand}08;
              transform: translateX(2px);

              &::before {
                opacity: 1;
              }

              .suggestion-icon {
                transform: scale(1.1);
                background: ${tokens.brand}25;
              }

              .suggestion-name {
                color: ${tokens.brand};
              }
            }

            &.loading {
              justify-content: center;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              font-style: italic;
              cursor: default;
              padding: 20px;
              
              &:hover {
                background: transparent;
                transform: none;
              }
            }

            .suggestion-icon {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: ${tokens.brand}15;
              color: ${tokens.brand};
              flex-shrink: 0;
              transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
              
              svg {
                font-size: 1.2em;
              }
            }

            .suggestion-info {
              display: flex;
              flex-direction: column;
              gap: 4px;
              flex: 1;
              min-width: 0;

              .suggestion-name {
                font-weight: 700;
                font-size: 0.92em;
                color: ${({ theme }) => theme.text};
                transition: color 0.2s ease;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }

              .suggestion-cnpj {
                font-size: 0.78em;
                font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
                color: ${({ theme }) => theme.text};
                opacity: 0.55;
                letter-spacing: 0.3px;
              }
            }
          }
        }

        .filter-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.8;

          svg {
            font-size: 1em;
            opacity: 0.6;
          }
        }

        input,
        select {
          width: 100%;
          padding: 10px 12px;
          background: ${({ theme }) => surface(theme, 1)};
          border: 1px solid ${({ theme }) => hairline(theme)};
          border-radius: 6px;
          color: ${({ theme }) => theme.text};
          font-size: 0.85em;
          transition: all 0.2s ease;

          &:focus {
            outline: none;
            border-color: ${tokens.brand};
            box-shadow: 0 0 0 2px ${tokens.brand}20;
          }

          &::placeholder {
            color: ${({ theme }) => theme.text};
            opacity: 0.4;
          }
        }

        select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 36px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;

          input {
            flex: 1;
          }

          span {
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }
        }

        .filter-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;

          .filter-badge {
            padding: 8px 16px;
            background: ${({ theme }) => surface(theme, 1)};
            border: 1px solid ${({ theme }) => hairline(theme)};
            border-radius: 6px;
            color: ${({ theme }) => theme.text};
            font-size: 0.85em;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            opacity: 0.6;

            &:hover {
              opacity: 1;
              border-color: ${tokens.brand};
              background: ${tokens.brand}10;
            }

            &.active {
              opacity: 1;
              background: ${tokens.brand};
              color: white;
              border-color: ${tokens.brand};
            }
          }
        }
      }
    }


      .filter-section {
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: 12px;
        overflow: visible;
        background: ${({ theme }) => surface(theme, 1)};
        min-height: max-content;

        .filter-section-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: ${({ theme }) => surface(theme, 0)};
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
          font-weight: 600;
          font-size: 0.9em;
          color: ${({ theme }) => theme.text};

          svg {
            font-size: 1.1em;
            opacity: 0.7;
          }
        }

        .filter-section-content {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: max-content;
          overflow: visible;
        }

        .selected-count {
          margin-left: auto;
          font-size: 0.8em;
          padding: 4px 10px;
          background: rgba(16, 185, 129, 0.15);
          color: ${tokens.brand};
          border-radius: 12px;
          font-weight: 500;
        }
      }

      /* Estilos para o filtro de empresas */
      .empresas-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-height: 400px;
        overflow-y: auto;
        overflow-x: hidden;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: ${({ theme }) => surface(theme, 1)};
          border-radius: 12px;
        }

        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme)};
          border-radius: 12px;

          &:hover {
            background: ${({ theme }) => theme.text};
            opacity: 0.3;
          }
        }

        .empresas-loading,
        .empresas-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;

          svg {
            font-size: 48px;
            margin-bottom: 12px;
          }

          p {
            margin: 0;
            font-size: 0.9em;
          }
        }

        .empresas-list {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .empresa-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 14px;
            background: ${({ theme }) => surface(theme, 0)};
            border: 1px solid ${({ theme }) => hairline(theme)};
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background: ${({ theme }) => surface(theme, 1)};
              border-color: ${({ theme }) => hairline(theme)};
              transform: translateX(4px);
            }

            &.selected {
              background: rgba(16, 185, 129, 0.1);
              border-color: rgba(16, 185, 129, 0.3);

              &:hover {
                background: rgba(16, 185, 129, 0.15);
                border-color: rgba(16, 185, 129, 0.4);
              }

              .empresa-icon {
                background: rgba(16, 185, 129, 0.15);
                border-color: rgba(16, 185, 129, 0.3);

                svg {
                  color: ${tokens.brand};
                  opacity: 1;
                }
              }
            }

            .empresa-icon {
              width: 40px;
              height: 40px;
              border-radius: 12px;
              background: ${({ theme }) => theme.body === '#fafbfc' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'};
              border: 1px solid transparent;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
              transition: all 0.2s ease;

              svg {
                font-size: 20px;
                color: ${({ theme }) => theme.text};
                opacity: 0.5;
              }
            }

            .empresa-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 4px;
              overflow: hidden;

              .empresa-nome {
                font-size: 0.85em;
                font-weight: 600;
                color: ${({ theme }) => theme.text};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }

              .empresa-cnpj {
                font-size: 0.75em;
                color: ${({ theme }) => theme.text};
                opacity: 0.5;
                font-family: 'Courier New', monospace;
              }
            }

            .empresa-check {
              flex-shrink: 0;

              svg {
                font-size: 22px;
                color: ${tokens.brand};
                filter: drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3));
              }
            }
          }
        }
      }
    .filter-sidebar-footer {
      display: flex;
      gap: 12px;
      padding: 20px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      button {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 16px;
        border: none;
        border-radius: 6px;
        font-size: 0.85em;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;

        svg {
          font-size: 1em;
        }
      }

      .btn-clear {
        background: ${({ theme }) => surface(theme, 1)};
        color: ${({ theme }) => theme.text};
        border: 1px solid ${({ theme }) => hairline(theme)};

        &:hover {
          background: ${({ theme }) => hairline(theme)};
        }
      }

      .btn-apply {
        background: ${tokens.brand};
        color: #fff;

        &:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      }
    }
  }

  /* ========== FILTER OVERLAY ========== */
  .filter-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
